import { Cartesian3, Color, Quaternion, createGuid, } from "cesium";
import { EarthBrainUtils } from "../../utilities/earthbrainutility";
import { SolidMesh, resolveLocalFrame } from "./solid-mesh";
import { enforceAnchorConstraint } from "./solid-mesh-helper";
/** Identity translation (0, 0, 0) */
const IDENTITY_TRANSLATION = new Cartesian3(0, 0, 0);
/** Check if translation is identity */
function isIdentityTranslation(translation) {
    return Cartesian3.equalsEpsilon(translation, IDENTITY_TRANSLATION, 1e-10);
}
async function resolveShapeCenters(shapeConfig, crsConverter) {
    const resolvedShapeConfig = { ...shapeConfig };
    const wgs84CenterWasPrimary = !!shapeConfig.wgs84Center &&
        (!shapeConfig.localCenter ||
            isIdentityTranslation(shapeConfig.localCenter));
    // Track which coordinate system was the primary input
    const localCenterWasPrimary = !shapeConfig.wgs84Center && !!shapeConfig.localCenter;
    // Skip conversion if bothCentersExist is false (loaded from saved data)
    if (wgs84CenterWasPrimary) {
        // Primary input: wgs84Center -> compute localCenter
        if (resolvedShapeConfig.wgs84Center) {
            const localCenters = await crsConverter.toLocal([
                resolvedShapeConfig.wgs84Center,
            ]);
            resolvedShapeConfig.localCenter = localCenters[0];
        }
        // Fallback: localCenter -> compute wgs84Center
        else if (resolvedShapeConfig.localCenter) {
            const wgs84Centers = await crsConverter.toWgs84([
                resolvedShapeConfig.localCenter,
            ]);
            resolvedShapeConfig.wgs84Center = wgs84Centers[0];
        }
    }
    if (localCenterWasPrimary) {
        const wgs84Centers = await crsConverter.toWgs84([
            resolvedShapeConfig.localCenter,
        ]);
        resolvedShapeConfig.wgs84Center = wgs84Centers[0];
    }
    return {
        shapeConfig: resolvedShapeConfig,
        bothCentersExist: wgs84CenterWasPrimary,
        localCenterWasPrimary,
    };
}
/**
 * Create seam: the constructor already composed modelMatrix from the frame,
 * so only a raw ECEF translation and the localCenter settle remain.
 */
async function applyInitialTransforms(params) {
    const { solidMesh, shapeConfig, localCenterWasPrimary } = params;
    // Translation is a raw ECEF vector: localCenter cannot be derived
    // synchronously, so settle with ONE conversion at the end of the seam.
    if (shapeConfig.translation &&
        !isIdentityTranslation(shapeConfig.translation)) {
        solidMesh.translatePreviewWgs84(shapeConfig.translation);
    }
    // Settle localCenter against the converter (single conversion for the whole
    // seam; converter is ground truth for the created state)
    if (solidMesh.isLocalCenterStale || !localCenterWasPrimary) {
        await solidMesh.settleLocalCenterFromWgs84();
    }
}
async function buildSolidMesh(options, config) {
    const { cesiumViewer, shapeConfig, id, attributes, crsConverter } = options;
    const newId = id ?? createGuid();
    const newAttributes = {
        anchorType: "bottom-nw",
        ...attributes,
    };
    const { shapeConfig: resolvedShapeConfig, bothCentersExist, localCenterWasPrimary, } = await resolveShapeCenters(shapeConfig, crsConverter);
    const newShapeConfig = {
        ...config.defaultShapeConfig,
        ...resolvedShapeConfig,
    };
    // Saved/resolved wgs84Center is the frame's ECEF origin: loading adds no
    // local→WGS84 round trip for the center
    const localFrame = await resolveLocalFrame(crsConverter, newShapeConfig.localCenter, newShapeConfig.wgs84Center);
    const primitive = config.createPrimitive(newShapeConfig, newId);
    const addPrimitiveFn = () => {
        cesiumViewer.viewer.scene.primitives.add(primitive);
    };
    const removePrimitiveFn = () => {
        return cesiumViewer.viewer.scene.primitives.remove(primitive);
    };
    const solidMesh = new SolidMesh({
        id: newId,
        shapeConfig: newShapeConfig,
        renderedObject: primitive,
        addPrimitiveFn,
        removePrimitiveFn,
        crsConverter,
        localFrame,
        attributes: newAttributes,
        baselineQuaternion: options.baselineQuaternion,
        visible: options.visible,
    });
    if (!bothCentersExist) {
        await applyInitialTransforms({
            solidMesh,
            shapeConfig: newShapeConfig,
            localCenterWasPrimary,
        });
        enforceAnchorConstraint(solidMesh);
    }
    return solidMesh;
}
/** Default transform values */
export const DEFAULT_TRANSFORM = {
    translation: new Cartesian3(0, 0, 0),
    quaternion: new Quaternion(0, 0, 0, 1),
    scale: new Cartesian3(1, 1, 1),
};
/** Default material values */
export const DEFAULT_MATERIAL = {
    color: new Color(1, 1, 1, 1),
};
const DEFAULT_DIMENSIONS = new Cartesian3(10, 10, 10);
/** Default box parameters (10m cube) */
export const DEFAULT_BOX_PARAMS = {
    shapeType: "box",
    localCenter: new Cartesian3(0, 0, 0),
    wgs84Center: new Cartesian3(0, 0, 0),
    dimensions: DEFAULT_DIMENSIONS,
    ...DEFAULT_MATERIAL,
    ...DEFAULT_TRANSFORM,
};
/**
 * Builder for box-shaped SolidMesh
 * Expects wgs84Center or localCenter as input (caller converts anchor to center)
 */
class BoxBuilder {
    /**
     * Builds a box SolidMesh
     * @param options - Build options
     * @returns Promise resolving to the created box SolidMesh
     */
    async build(options) {
        return buildSolidMesh(options, {
            defaultShapeConfig: DEFAULT_BOX_PARAMS,
            createPrimitive: (shapeConfig, newId) => EarthBrainUtils.createBoxPrimitive(shapeConfig.wgs84Center, shapeConfig.dimensions, shapeConfig.color, newId),
        });
    }
}
/** Default sphere parameters (10m diameter sphere) */
export const DEFAULT_SPHERE_PARAMS = {
    shapeType: "sphere",
    localCenter: new Cartesian3(0, 0, 0),
    wgs84Center: new Cartesian3(0, 0, 0),
    dimensions: DEFAULT_DIMENSIONS,
    ...DEFAULT_MATERIAL,
    ...DEFAULT_TRANSFORM,
};
/** Default cylinder parameters (10m diameter, 10m height) */
export const DEFAULT_CYLINDER_PARAMS = {
    shapeType: "cylinder",
    localCenter: new Cartesian3(0, 0, 0),
    wgs84Center: new Cartesian3(0, 0, 0),
    dimensions: DEFAULT_DIMENSIONS,
    ...DEFAULT_MATERIAL,
    ...DEFAULT_TRANSFORM,
};
/**
 * Builder for sphere-shaped SolidMesh
 * Expects wgs84Center or localCenter as input (caller converts anchor to center)
 */
class SphereBuilder {
    /**
     * Builds a sphere SolidMesh
     * @param options - Build options
     * @returns Promise resolving to the created sphere SolidMesh
     */
    async build(options) {
        return buildSolidMesh(options, {
            defaultShapeConfig: DEFAULT_SPHERE_PARAMS,
            createPrimitive: (shapeConfig, newId) => EarthBrainUtils.createEllipsoidPrimitive(shapeConfig.wgs84Center, shapeConfig.dimensions, shapeConfig.color, newId),
        });
    }
}
/**
 * Builder for cylinder-shaped SolidMesh
 * Expects wgs84Center or localCenter as input (caller converts anchor to center)
 */
class CylinderBuilder {
    /**
     * Builds a cylinder SolidMesh
     * @param options - Build options
     * @returns Promise resolving to the created cylinder SolidMesh
     */
    async build(options) {
        return buildSolidMesh(options, {
            defaultShapeConfig: DEFAULT_CYLINDER_PARAMS,
            createPrimitive: (shapeConfig, newId) => EarthBrainUtils.createCylinderPrimitive(shapeConfig.wgs84Center, shapeConfig.dimensions, shapeConfig.color, newId),
        });
    }
}
/** Map of shape types to their builders */
const builders = new Map([
    ["box", new BoxBuilder()],
    ["sphere", new SphereBuilder()],
    ["cylinder", new CylinderBuilder()],
]);
/**
 * Builds a SolidMesh using the appropriate builder based on shape type
 * @template T - Shape configuration type
 * @param options - Build options containing viewer, shapeConfig, id, and attributes
 * @returns Promise resolving to the created SolidMesh or undefined
 */
export async function buildSolidMeshWithBuilder(options) {
    const builder = builders.get(options.shapeConfig.shapeType);
    if (builder) {
        return (await builder.build(options));
    }
    console.error(`No builder found for shape type: ${options.shapeConfig.shapeType}`);
    return undefined;
}
//# sourceMappingURL=solid-mesh-builder.js.map